if (!customElements.get('custom-featured-collection')) {
  customElements.define(
    'custom-featured-collection',
    class CustomFeaturedCollection extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        this.productsBlock = this.querySelector('[data-products-block]');
        this.showAllLink = this.querySelector('[data-show-all-link]');
        this.scrollers = Array.from(this.querySelectorAll('.custom-featured-collection__scroller'));
        this.prevButton = this.querySelector('[data-carousel-prev]');
        this.nextButton = this.querySelector('[data-carousel-next]');
        this.animationTimeout = null;
        this.onClick = this.onClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onPrevClick = this.onPrevClick.bind(this);
        this.onNextClick = this.onNextClick.bind(this);
        this.onScrollerScroll = this.onScrollerScroll.bind(this);
        this.updateArrowState = this.updateArrowState.bind(this);

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', this.onClick);
          tab.addEventListener('keydown', this.onKeydown);
        });

        this.scrollers.forEach((scroller) => {
          scroller.addEventListener('scroll', this.onScrollerScroll, { passive: true });
        });

        this.prevButton?.addEventListener('click', this.onPrevClick);
        this.nextButton?.addEventListener('click', this.onNextClick);
        window.addEventListener('resize', this.updateArrowState);
        this.updateArrowState();
      }

      disconnectedCallback() {
        this.tabs.forEach((tab) => {
          tab.removeEventListener('click', this.onClick);
          tab.removeEventListener('keydown', this.onKeydown);
        });
        this.scrollers.forEach((scroller) => {
          scroller.removeEventListener('scroll', this.onScrollerScroll);
        });
        this.prevButton?.removeEventListener('click', this.onPrevClick);
        this.nextButton?.removeEventListener('click', this.onNextClick);
        window.removeEventListener('resize', this.updateArrowState);
        window.clearTimeout(this.animationTimeout);
      }

      onClick(event) {
        this.selectTab(event.currentTarget);
      }

      onKeydown(event) {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          return;
        }

        event.preventDefault();

        const currentIndex = this.tabs.indexOf(event.currentTarget);
        let nextIndex = currentIndex;

        if (event.key === 'ArrowLeft') {
          nextIndex = currentIndex === 0 ? this.tabs.length - 1 : currentIndex - 1;
        } else if (event.key === 'ArrowRight') {
          nextIndex = currentIndex === this.tabs.length - 1 ? 0 : currentIndex + 1;
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = this.tabs.length - 1;
        }

        this.selectTab(this.tabs[nextIndex]);
        this.tabs[nextIndex].focus();
      }

      onPrevClick() {
        this.scrollActivePanel(-1);
      }

      onNextClick() {
        this.scrollActivePanel(1);
      }

      onScrollerScroll() {
        this.updateArrowState();
      }

      selectTab(selectedTab) {
        const selectedPanelId = selectedTab.getAttribute('aria-controls');
        const currentPanel = this.panels.find((panel) => panel.getAttribute('aria-hidden') === 'false');

        if (!selectedPanelId || currentPanel?.id === selectedPanelId) {
          return;
        }

        this.tabs.forEach((tab) => {
          const isSelected = tab === selectedTab;
          tab.setAttribute('aria-selected', String(isSelected));
          tab.tabIndex = isSelected ? 0 : -1;
        });

        if (!this.productsBlock) {
          this.switchPanel(selectedPanelId);
          return;
        }

        this.productsBlock.classList.add('is-changing');
        window.clearTimeout(this.animationTimeout);

        this.animationTimeout = window.setTimeout(() => {
          this.switchPanel(selectedPanelId);
          this.productsBlock.classList.remove('is-changing');
          this.updateArrowState();
        }, 180);
      }

      switchPanel(selectedPanelId) {
        this.panels.forEach((panel) => {
          panel.setAttribute('aria-hidden', String(panel.id !== selectedPanelId));
        });

        this.updateShowAllLink(selectedPanelId);
      }

      updateShowAllLink(selectedPanelId) {
        if (!this.showAllLink) {
          return;
        }

        const url = selectedPanelId?.endsWith('-rich')
          ? this.showAllLink.getAttribute('data-rich-url')
          : this.showAllLink.getAttribute('data-light-url');

        if (url) {
          this.showAllLink.href = url;
        }
      }

      getActiveScroller() {
        return this.querySelector('[role="tabpanel"][aria-hidden="false"] .custom-featured-collection__scroller');
      }

      getScrollAmount(scroller) {
        const firstCard = scroller?.querySelector('.custom-featured-collection__card');

        if (!firstCard) {
          return Math.round((scroller?.clientWidth || 0) * 0.85);
        }

        const list = scroller.querySelector('.custom-featured-collection__product-list');
        const gap = list ? parseFloat(window.getComputedStyle(list).columnGap || '0') : 0;

        return Math.round(firstCard.getBoundingClientRect().width + gap);
      }

      scrollActivePanel(direction) {
        const scroller = this.getActiveScroller();

        if (!scroller) {
          return;
        }

        scroller.scrollBy({
          left: this.getScrollAmount(scroller) * direction,
          behavior: 'smooth',
        });
      }

      updateArrowState() {
        const scroller = this.getActiveScroller();

        if (!this.prevButton || !this.nextButton || !scroller) {
          return;
        }

        const canScroll = scroller.scrollWidth > scroller.clientWidth + 1;
        const atStart = scroller.scrollLeft <= 1;
        const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;

        this.prevButton.disabled = !canScroll || atStart;
        this.nextButton.disabled = !canScroll || atEnd;
      }
    }
  );
}
